import { useEffect } from 'react';
import { useUser } from '@clerk/clerk-react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useConversations } from './use-conversations';
import { useSubscribeToNotifications } from '@/lib/websocket/realtime-chat';

/**
 * Global hook to subscribe to all conversations for notifications
 * This ensures users get notified of new messages even when not on the chat page
 */
export function useGlobalChatNotifications() {
  const { user } = useUser();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { conversations } = useConversations();

  // Helper function to show browser notifications
  const showNotification = (title: string, body: string, conversationId: string) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      const notification = new Notification(title, {
        body,
        icon: '/apple-touch-icon.png',
        tag: conversationId, // Prevent duplicate notifications
      });

      // Navigate to chat when notification is clicked
      notification.onclick = () => {
        window.focus();
        // Determine chat route based on current path
        const isGenius = window.location.pathname.startsWith('/genius');
        const chatRoute = isGenius ? '/genius/chat' : '/client/chat';
        navigate(`${chatRoute}?conversationId=${conversationId}`);
        notification.close();
      };
    }
  };

  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Subscribe to notifications for all conversations via WebSocket
  useSubscribeToNotifications((notification) => {
    // Only handle notifications for messages from other users
    if (notification.sender_id === user?.id) return;

    // Find the conversation
    const conversation = conversations.find((c) => c.id === notification.conversation_id);
    if (!conversation) return;

    // Invalidate conversations to update last message preview
    queryClient.invalidateQueries({ queryKey: ['conversations'] });

    // Invalidate messages query to trigger refetch (or the message will come via useSubscribeToMessages if user is on chat page)
    queryClient.invalidateQueries({ queryKey: ['messages', notification.conversation_id] });

    // Show notification if window is not focused or user is not on chat page
    const isOnChatPage = window.location.pathname.includes('/chat');
    if (document.hidden || !isOnChatPage) {
      const otherParticipant = conversation.participants.find(
        (p) => p.user_id !== user?.id
      );
      const senderName =
        otherParticipant?.role === 'genius' ? 'Freelancer' : 'Client';
      showNotification(
        `New message from ${senderName}`,
        notification.preview,
        conversation.id
      );
    }
  });

  return null;
}
